import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CreditCard, 
  Shield, 
  CheckCircle, 
  Phone, 
  FileText,
  DollarSign,
  Users,
  Heart,
  Calendar,
  AlertCircle
} from 'lucide-react';

const acceptedInsurance = [
  {
    category: "Government Insurance",
    icon: Shield,
    color: "bg-blue-100 text-blue-800",
    plans: [
      {
        name: "Medicare",
        parts: ["Part A", "Part B", "Part C (Medicare Advantage)", "Part D"],
        coverage: "Comprehensive psychiatric services covered",
        note: "Most services covered with minimal copay"
      },
      {
        name: "Medicaid",
        parts: ["Traditional Medicaid", "Medicaid Managed Care"],
        coverage: "Full coverage for eligible services",
        note: "No copay for most psychiatric services"
      },
      {
        name: "TRICARE",
        parts: ["TRICARE Prime", "TRICARE Select", "TRICARE Reserve Select"],
        coverage: "Mental health services covered",
        note: "Military and veteran families"
      }
    ]
  },
  {
    category: "Major Private Insurance",
    icon: Heart,
    color: "bg-green-100 text-green-800",
    plans: [
      {
        name: "Blue Cross Blue Shield",
        parts: ["All BCBS Plans", "Federal Employee Program"],
        coverage: "Comprehensive mental health coverage",
        note: "Coverage varies by specific plan"
      },
      {
        name: "Aetna",
        parts: ["Aetna Better Health", "CVS Health Plans"],
        coverage: "Mental health and substance abuse",
        note: "Prior authorization may be required"
      },
      {
        name: "UnitedHealthcare",
        parts: ["UHC Community Plan", "OptumCare"],
        coverage: "Behavioral health services",
        note: "Extensive provider network"
      },
      {
        name: "Cigna",
        parts: ["Cigna HealthCare", "Evernorth"],
        coverage: "Mental health and wellness",
        note: "Preventive care emphasized"
      }
    ]
  },
  {
    category: "Regional & Specialized Insurance",
    icon: Users,
    color: "bg-purple-100 text-purple-800",
    plans: [
      {
        name: "Humana",
        parts: ["Humana Medicare", "Humana Medicaid"],
        coverage: "Senior-focused mental health",
        note: "Specialized geriatric programs"
      },
      {
        name: "Kaiser Permanente",
        parts: ["Kaiser Foundation Health Plan"],
        coverage: "Integrated care model",
        note: "Coordinated with primary care"
      },
      {
        name: "Molina Healthcare",
        parts: ["Molina Medicaid", "Marketplace Plans"],
        coverage: "Community-based care",
        note: "Focus on underserved populations"
      },
      {
        name: "WellCare",
        parts: ["WellCare Medicare", "WellCare Medicaid"],
        coverage: "Government program specialist",
        note: "Enhanced benefits for chronic conditions"
      }
    ]
  }
];

const paymentOptions = [
  {
    title: "Insurance Billing",
    description: "We handle all insurance billing and prior authorizations",
    icon: Shield,
    features: ["Direct insurance billing", "Prior authorization assistance", "Claims management", "Coverage verification"]
  },
  {
    title: "Self-Pay Options",
    description: "Competitive rates for uninsured or out-of-network patients",
    icon: DollarSign,
    features: ["Transparent pricing", "Payment plans available", "Cash discounts", "Financial assistance programs"]
  },
  {
    title: "Employee Assistance Programs",
    description: "Contracted with major EAP providers",
    icon: Users,
    features: ["Corporate wellness programs", "Employee mental health", "Confidential services", "Workplace consultations"]
  },
  {
    title: "Government Programs",
    description: "Specialized billing for government healthcare programs",
    icon: FileText,
    features: ["VA/Military benefits", "Workers' compensation", "State disability programs", "Federal employee plans"]
  }
];

const verificationProcess = [
  {
    step: 1,
    title: "Insurance Verification",
    description: "We verify your coverage before your first appointment",
    icon: CheckCircle
  },
  {
    step: 2,
    title: "Benefits Review",
    description: "We'll explain your copays, deductibles, and coverage limits",
    icon: FileText
  },
  {
    step: 3,
    title: "Prior Authorization",
    description: "We handle any required prior authorizations for your care",
    icon: Calendar
  },
  {
    step: 4,
    title: "Direct Billing",
    description: "We bill your insurance directly - no claim forms for you",
    icon: CreditCard
  }
];



export const InsuranceInfo = ({ 
  showPaymentOptions = true, 
  showVerificationProcess = true,
  compact = false 
}) => {
  return (
    <div className="space-y-8">
      {/* Accepted Insurance */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Shield className="icon-lg text-clinic-blue-600" />
            Accepted Insurance Plans
          </CardTitle>
          <CardDescription>
            We accept most major insurance plans and work with you to maximize your benefits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {acceptedInsurance.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container icon-container-md icon-medical">
                    <category.icon className="icon-lg" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-800">{category.category}</h3>
                  <Badge className={category.color}>{category.plans.length} Plans</Badge>
                </div>
                
                <div className={`grid gap-4 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                  {category.plans.map((plan, planIndex) => (
                    <Card key={planIndex} className="border-l-4 border-l-clinic-blue-500">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <CardDescription className="text-sm">{plan.coverage}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {plan.parts.map((part, partIndex) => (
                            <div key={partIndex} className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-clinic-gray-600">{part}</span>
                            </div>
                          ))}
                        </div>
                        {plan.note && (
                          <div className="mt-3 p-2 bg-clinic-blue-50 rounded-lg">
                            <p className="text-xs text-clinic-blue-700">{plan.note}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Important Notice */}
      <Card className="border-l-4 border-l-amber-500 bg-amber-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">Insurance Coverage Note</h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                Insurance coverage can vary significantly between specific plans within the same insurance company. 
                We recommend calling our office to verify your specific benefits and discuss any potential out-of-pocket costs 
                before your first appointment. Our billing specialists are happy to help you understand your coverage.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Options */}
      {showPaymentOptions && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <CreditCard className="icon-lg text-clinic-blue-600" />
              Payment Options & Financial Assistance
            </CardTitle>
            <CardDescription>
              Multiple payment options to ensure access to mental healthcare
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {paymentOptions.map((option, index) => (
                <Card key={index} className="border border-clinic-gray-200">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="icon-container icon-container-md icon-medical">
                        <option.icon className="icon-lg" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{option.title}</CardTitle>
                        <CardDescription className="text-sm">{option.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {option.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-clinic-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verification Process */}
      {showVerificationProcess && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <CheckCircle className="icon-lg text-clinic-blue-600" />
              Insurance Verification Process
            </CardTitle>
            <CardDescription>
              How we make insurance easy for you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {verificationProcess.map((item) => (
                <div key={item.step} className="text-center">
                  <div className="icon-container icon-container-xl icon-medical mx-auto mb-4">
                    <item.icon className="icon-2xl" />
                  </div>
                  <div className="bg-clinic-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-clinic-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact for Questions */}
      <Card className="bg-clinic-blue-600 text-white">
        <CardContent className="p-6">
          <div className="text-center">
            <Phone className="icon-2xl mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Questions About Your Insurance?</h3>
            <p className="mb-4">Our billing specialists are here to help you understand your coverage</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span>(555) 123-4567</span>
              </div>
              <div className="flex items-center gap-2">
                <span>billing@psychiatriccare.com</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};